// src/Sidebar.js
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import './styles/Sidebar.css';

const iconBaseProps = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

const navIcons = {
  dashboard: (
    <svg {...iconBaseProps}>
      <rect x="3.5" y="13.5" width="5.5" height="6.5" rx="1" />
      <rect x="10" y="4.5" width="5.5" height="15.5" rx="1" />
      <rect x="16.5" y="9.5" width="4.5" height="10.5" rx="1" />
    </svg>
  ),
  users: (
    <svg {...iconBaseProps}>
      <path d="M4.5 20v-1.2c0-2.8 2.2-5 5-5h1c2.8 0 5 2.2 5 5V20" />
      <path d="M15.5 20v-1c0-2 1.6-3.6 3.6-3.6h0.4c1.7 0 3.1 1.4 3.1 3.1V20" />
      <circle cx="9.5" cy="8" r="3.2" />
      <circle cx="17" cy="10" r="2.7" />
    </svg>
  ),
  token: (
    <svg {...iconBaseProps}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M14.5 11.5l5-5a2 2 0 1 0-2.8-2.8l-5 5" />
      <path d="M13.5 6.5l4 4" />
      <path d="M9 11.5v1.5" />
      <path d="M9 3v1" />
    </svg>
  ),
  charts: (
    <svg {...iconBaseProps}>
      <path d="M4 18.5h16" />
      <path d="M6.2 15.3l3.1-3.4 3 2.6 5.5-6.5" />
      <circle cx="6.5" cy="15" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="9.3" cy="12" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="14.3" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="17.8" cy="7.9" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  export: (
    <svg {...iconBaseProps}>
      <path d="M12 4v10" />
      <path d="M8.5 11.5L12 15l3.5-3.5" />
      <path d="M6 18.5h12" />
      <path d="M6 15.5v4" />
      <path d="M18 15.5v4" />
    </svg>
  ),
  products: (
    <svg {...iconBaseProps}>
      <path d="M6 7.5l2.4-3.2A1.5 1.5 0 0 1 9.6 3.5h4.8a1.5 1.5 0 0 1 1.2.6L18 7.5" />
      <path d="M5.5 9h13" />
      <path d="M7 9v9.5a1.5 1.5 0 0 0 1.5 1.5h7a1.5 1.5 0 0 0 1.5-1.5V9" />
      <path d="M10 13h4" />
    </svg>
  ),
  currencies: (
    <svg {...iconBaseProps}>
      <circle cx="11" cy="11" r="5.5" />
      <path d="M11 6v10" />
      <path d="M8.5 9.5h3.2a2.3 2.3 0 1 1 0 4.6H8" />
      <path d="M17.5 7.5l3 3-3 3" />
      <path d="M14.5 10.5h5.8" />
    </svg>
  ),
  news: (
    <svg {...iconBaseProps}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 8h8" />
      <path d="M8 12h8" />
      <path d="M8 16h4" />
    </svg>
  ),
  analytics: (
    <svg {...iconBaseProps}>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M7 16l4-8 4 4 4-10" />
      <circle cx="7" cy="16" r="1.5" fill="currentColor" />
      <circle cx="11" cy="8" r="1.5" fill="currentColor" />
      <circle cx="15" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="2" r="1.5" fill="currentColor" />
    </svg>
  ),
  agences: (
    <svg {...iconBaseProps}>
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
      <path d="M9 9h1" />
      <path d="M14 9h1" />
      <path d="M9 13h1" />
      <path d="M14 13h1" />
    </svg>
  ),
  metals: (
    <svg {...iconBaseProps}>
      <path d="M3.5 18.5h17" />
      <path d="M5 14.5l4.5-9 3.5 6 3.5-3 3.5 6" />
      <circle cx="9.5" cy="5.5" r="1.2" />
      <circle cx="16" cy="7" r="1.2" />
    </svg>
  ),
  marges: (
    <svg {...iconBaseProps}>
      <path d="M5 19L19 5" />
      <circle cx="7.5" cy="7.5" r="2.5" />
      <circle cx="16.5" cy="16.5" r="2.5" />
    </svg>
  ),
};

const Sidebar = () => {
  const [role, setRole] = useState('');
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios
        .get('/api/member/profile', {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => setRole(res.data.role))
        .catch((err) => console.error(err));
    }
  }, []);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const menuItems = [
    {
      path: '/dashboard',
      icon: 'dashboard',
      label: 'Dashboard',
      visible: true,
    },
    {
      path: '/user-management',
      icon: 'users',
      label: 'Utilisateurs',
      visible: role === 'admin',
    },
    {
      path: '/create-token',
      icon: 'token',
      label: 'Tokens API',
      visible: true,
    },
    {
      path: '/charts',
      icon: 'charts',
      label: 'Graphiques',
      visible: true,
    },
    {
      path: '/export-csv',
      icon: 'export',
      label: 'Export CSV',
      visible: true,
    },
    {
      path: '/products',
      icon: 'products',
      label: 'Produits',
      visible: true,
    },
    {
      path: '/currencies',
      icon: 'currencies',
      label: 'Devises',
      visible: true,
    },
    {
      path: '/metals/live',
      icon: 'metals',
      label: 'Cours Live',
      visible: true,
    },
    {
      path: '/news',
      icon: 'news',
      label: 'Actualités',
      visible: true,
    },
    {
      path: '/agences',
      icon: 'agences',
      label: 'Agences',
      visible: true,
    },
    {
      path: '/marges',
      icon: 'marges',
      label: 'Marges',
      visible: true,
    },
    {
      path: '/analytics',
      icon: 'analytics',
      label: 'Analytics API',
      visible: true,
    },
  ];

  return (
    <aside className="sidebar" role="navigation" aria-label="Navigation principale">
      <div className="sidebar__brand">
        <div className="sidebar__brand-icon" aria-hidden="true">
          <span>⚜</span>
        </div>
        <div className="sidebar__brand-text">
          <span className="sidebar__brand-title">Achat Or</span>
          <span className="sidebar__brand-subtitle">& Argent</span>
        </div>
      </div>

      <nav className="sidebar__nav">
        <ul className="sidebar__list">
          {menuItems.map(
            (item) =>
              item.visible && (
                <li key={item.path} className="sidebar__item">
                  <Link
                    to={item.path}
                    className={`sidebar__link ${isActive(item.path) ? 'is-active' : ''}`}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                  >
                    <span className="sidebar__item-icon">{navIcons[item.icon]}</span>
                    <span className="sidebar__item-label">{item.label}</span>
                  </Link>
                </li>
              )
          )}
        </ul>
      </nav>

      <footer className="sidebar__footer">
        <div className="sidebar__status">
          <div className="sidebar__status-icon" aria-hidden="true">
            <span>⚡</span>
          </div>
          <div className="sidebar__status-text">
            <span className="sidebar__status-title">API Status</span>
            <span className="sidebar__status-value is-online">En ligne</span>
          </div>
        </div>
      </footer>
    </aside>
  );
};

export default Sidebar;